import {SYNC_GAME_DEBOUNCE} from './GameOperationsService.constants';
import {DI_TOKENS} from '@/di/di.tokens';
import {AnalyticsFirstImprovementType} from '@/services/TrackingService/TrackingService.constants';
import {OperationType} from '@/stores/ClickerStateStore/ClickerStateStore.constants';
import {
  ClickerState,
  ClickerStateStoreState,
  GameOperation,
  RechargeEnergyOperation,
  SpendEnergyOperation,
  UpgradeImprovementOperation,
  UpgradeImprovementWithoutUnfreezeOperation,
} from '@/stores/ClickerStateStore/ClickerStateStore.types';
// import cloneDeep from 'lodash.clonedeep';
import debounce from 'lodash.debounce';

import {injected} from 'brandi';

import {NullStorage, RealStorage} from '@specials/localstorage';
import {HttpError} from '@specials/project-user';

import {LocalStorageKeys} from '@/constants';
import {ErrorsService} from '@/services/ErrorsService/ErrorsService';
import {LoggerService} from '@/services/LoggerService/LoggerService';
import {IProjectApi} from '@/services/ProjectApi/types';
import {TrackingService} from '@/services/TrackingService/TrackingService';
import {GameStatusService} from '@/services/shared/GameStatusService/GameStatusService';
import {ClickerStateStore} from '@/stores/ClickerStateStore/ClickerStateStore';
import {ImprovementsStore} from '@/stores/ImprovementsStore/ImprovementsStore';
import {ArrayOperations} from '@/utils/ArrayOperations';

import {ClickerIncomeUpdater} from '../../ClickerIncomeUpdater/ClickerIncomeUpdater';
import {ProjectDate} from '../../ProjectDate/ProjectDate';

export class GameOperationsService {
  private queueOperations: GameOperation[];

  constructor(
    private readonly clickerStateStore: ClickerStateStore,
    private readonly loggerService: LoggerService,
    private readonly trackingService: TrackingService,
    private readonly improvementsStore: ImprovementsStore,
    private readonly projectApi: IProjectApi,
    private readonly storage: RealStorage | NullStorage,
    private readonly clickerIncomeUpdater: ClickerIncomeUpdater,
    private readonly errorsService: ErrorsService,
    private readonly gameStatusService: GameStatusService,
    private readonly projectDate: ProjectDate,
  ) {
    this.queueOperations = [];
  }

  // набор кликов с синхронизацией по дебаунсу
  public pushSpendEnergyOperation(energyToSpend: number): void {
    if (!this.clickerStateStore.isEnoughEnergyToSpend(energyToSpend)) return;

    this.clickerStateStore.spendEnergy(energyToSpend);

    this.pushOperation({
      type: OperationType.SpendEnergy,
      energyToSpend,
      at: this.getCurrentDate(),
    });

    this.saveOperationsInStorage();

    this.debouncedSyncOperations();
  }

  // синхронизация сразу после покупки улучшения (покупка за x2 во время фриза)
  public async pushUpgradeImprovementOperation(improvementId: string): Promise<void> {
    const isTrackBuyImprovement =
      !this.improvementsStore.hasBoughtImprovement() && this.improvementsStore.isFirstImprovement(improvementId);

    this.pushOperation({
      type: OperationType.UpgradeImprovement,
      improvementId,
      at: this.getCurrentDate(),
    });

    await this.fetchGameSync();

    if (isTrackBuyImprovement) {
      this.trackingService.trackClickToFirstImprovement(AnalyticsFirstImprovementType.Buy);
    }
  }

  // синхронизация сразу после покупки улучшения (обычная покупка)
  public async pushUpgradeImprovementWithoutUnfreezeOperation(improvementId: string): Promise<void> {
    const isTrackBuyImprovement =
      !this.improvementsStore.hasBoughtImprovement() && this.improvementsStore.isFirstImprovement(improvementId);

    this.pushOperation({
      type: OperationType.UpgradeImprovementWithoutUnfreeze,
      improvementId,
      at: this.getCurrentDate(),
    });

    await this.fetchGameSync();

    if (isTrackBuyImprovement) {
      this.trackingService.trackClickToFirstImprovement(AnalyticsFirstImprovementType.Buy);
    }
  }

  // синхронизация сразу после восстановления батарейки
  public async pushRechargeEnergyOperation(): Promise<void> {
    this.pushOperation({
      type: OperationType.RechargeEnergy,
      at: this.getCurrentDate(),
    });

    await this.fetchGameSync();

    this.trackingService.trackRechargeEnergy();
  }

  // первый запрос при инициализации приложения
  public async init(): Promise<void> {
    this.queueOperations = this.getOperationListFromStorage();

    await this.fetchGameSync();

    this.clickerIncomeUpdater.startUpdateGame();
  }

  private pushOperation(operation: GameOperation): void {
    this.queueOperations.push(operation);
  }

  public syncGameOperations = (): void => {
    if (this.queueOperations.length === 0) return;

    this.debouncedSyncOperations.cancel();
    void this.fetchGameSync();
  };

  // запрос на синхронизацию
  private fetchGameSync = async (): Promise<void> => {
    // todo retry и обработка запроса при ошибке
    if (this.clickerStateStore.isLoadingFetchStatus() || this.clickerStateStore.isErrorFetchStatus()) return;

    try {
      this.storage.removeItem(LocalStorageKeys.GameOperations);

      this.clickerStateStore.setLoadingFetchStatus();

      const operationCount = this.queueOperations.length;

      const operationList = this.getPreparedSyncOperations();

      // const copyGameState = cloneDeep(this.clickerStateStore.getState());

      const {payload} = await this.projectApi.gameSync(operationList);

      // this.checkAndLogEqualsImprovements(operationList, copyGameState, payload);

      this.projectDate.setServerDate(payload.actualAt);

      const {improvements, ...clickerState} = payload;
      this.clickerStateStore.setState(clickerState);

      this.improvementsStore.setCurrentIncomePerHour(payload.totalIncomePerHour);
      this.improvementsStore.setImprovements(improvements);

      // срезаем операции в очереди, которые должны были синхронизироваться
      this.queueOperations = this.queueOperations.slice(operationCount);

      this.clickerStateStore.setIdleFetchStatus();
    } catch (error) {
      this.loggerService.logHttpError(error as HttpError);

      if (this.errorsService.isErrorRecalculationLeagues(error)) {
        this.gameStatusService.setStatusLeaguesRecalculation();
        this.clickerStateStore.setIdleFetchStatus();
        this.restoreClickerState();
      } else {
        this.clickerStateStore.setErrorFetchStatus();
      }
      throw error;
    }
  };

  private restoreClickerState(): void {
    this.queueOperations.forEach((o) => {
      if (GameOperationsService.isSpendEnergyOperation(o)) {
        this.clickerStateStore.addEnergy(o.energyToSpend);
        this.clickerStateStore.decreasePoints(o.energyToSpend * this.clickerStateStore.getMultiplier());
      }

      if (GameOperationsService.isRechargeEnergyOperation(o)) {
        this.clickerStateStore.addEnergyChargeCount(1);
      }
    });

    this.queueOperations = [];
  }

  private debouncedSyncOperations = debounce(this.syncGameOperations, SYNC_GAME_DEBOUNCE);

  public static isUpgradeImprovementOperation(operation: GameOperation): operation is UpgradeImprovementOperation {
    return operation.type === OperationType.UpgradeImprovement;
  }

  public static isUpgradeImprovementWithoutUnfreezeOperation(
    operation: GameOperation,
  ): operation is UpgradeImprovementWithoutUnfreezeOperation {
    return operation.type === OperationType.UpgradeImprovementWithoutUnfreeze;
  }

  public static isSpendEnergyOperation(operation: GameOperation): operation is SpendEnergyOperation {
    return operation.type === OperationType.SpendEnergy;
  }

  public static isRechargeEnergyOperation(operation: GameOperation): operation is RechargeEnergyOperation {
    return operation.type === OperationType.RechargeEnergy;
  }

  private getCurrentDate(): string {
    return this.projectDate.date.toISOString();
  }

  private getPreparedSyncOperations(): GameOperation[] {
    const result: GameOperation[] = [];
    const spendEnergyOperationList: SpendEnergyOperation[] = [];

    for (let i = 0; i < this.queueOperations.length; i++) {
      const operation = this.queueOperations[i];
      if (GameOperationsService.isSpendEnergyOperation(operation)) {
        spendEnergyOperationList.push(operation);
      } else {
        const mergedSpendEnergyOperation = this.mergeEnergyToSpendOperation(spendEnergyOperationList);

        if (mergedSpendEnergyOperation) {
          result.push(mergedSpendEnergyOperation);
          ArrayOperations.clear(spendEnergyOperationList);
        }

        result.push(operation);
      }
    }

    const mergedSpendEnergyOperation = this.mergeEnergyToSpendOperation(spendEnergyOperationList);

    if (mergedSpendEnergyOperation) {
      result.push(mergedSpendEnergyOperation);
    }

    return result;
  }

  private mergeEnergyToSpendOperation(operationList: SpendEnergyOperation[]): SpendEnergyOperation | undefined {
    const lastSpendEnergyOperation = ArrayOperations.last(operationList);

    if (lastSpendEnergyOperation) {
      const energyToSpend = operationList.reduce((energy, item) => energy + item.energyToSpend, 0);

      return {
        type: OperationType.SpendEnergy,
        energyToSpend,
        at: lastSpendEnergyOperation.at,
      };
    }
  }

  private getOperationListFromStorage(): GameOperation[] {
    const savedOperations = this.storage.getItem(LocalStorageKeys.GameOperations);

    if (savedOperations) {
      try {
        return JSON.parse(savedOperations) as GameOperation[];
      } catch {
        // если было что-то кривое в ls, то просто не синкаем
        return [];
      }
    }

    return [];
  }

  private saveOperationsInStorage(): void {
    this.storage.setItem(LocalStorageKeys.GameOperations, JSON.stringify(this.queueOperations));
  }

  private checkAndLogEqualsImprovements(
    operationList: GameOperation[],
    clickerGameStoreState: ClickerStateStoreState,
    clickerState: ClickerState,
  ): void {
    // больше одного улучшения не может быть в списке
    const operation = operationList.find((o) => GameOperationsService.isUpgradeImprovementOperation(o));

    if (operation) {
      const oldImprovement = this.improvementsStore
        .getState()
        .improvements.find(
          (i) => GameOperationsService.isUpgradeImprovementOperation(operation) && operation.improvementId === i.id,
        );
      const currentImprovement = clickerState.improvements.find(
        (i) => GameOperationsService.isUpgradeImprovementOperation(operation) && operation.improvementId === i.id,
      );

      // улучшение не прокачалось, пришло то же самое
      if (oldImprovement && currentImprovement && oldImprovement.currentLevel === currentImprovement.currentLevel) {
        this.loggerService.handleCustomError(
          'Improvement was not upgraded',
          new Error('Improvement was not upgraded'),
          {
            operationList,
            oldImprovement,
            currentImprovement,
            payload: clickerState,
            copyGameState: {...clickerGameStoreState, actualAt: this.projectDate.date.toISOString()},
          },
        );
      }
    }
  }
}

injected(
  GameOperationsService,
  DI_TOKENS.clickerStateStore,
  DI_TOKENS.loggerService,
  DI_TOKENS.trackingService,
  DI_TOKENS.improvementsStore,
  DI_TOKENS.projectApi,
  DI_TOKENS.localStorageService,
  DI_TOKENS.clickerIncomeUpdater,
  DI_TOKENS.errorsService,
  DI_TOKENS.gameStatusService,
  DI_TOKENS.projectDate,
);
